import React, { useMemo } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender
} from '@tanstack/react-table';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton
} from '@mui/material';
import { Visibility as ViewIcon } from '@mui/icons-material';
import { format } from 'date-fns';
import { formatCurrency } from '../../utils/calculations';

const ReportTransactionsTable = ({ title, data, totals, emptyMessage = 'No transactions found', onView }) => {
  const columns = useMemo(
    () => [
      {
        accessorKey: 'id',
        header: '#',
        cell: info => info.getValue(),
        size: 80
      },
      {
        accessorKey: 'customer',
        header: 'Customer/Account',
        cell: info => {
          const row = info.row.original;
          // Invoices/credit notes eager-load `account` (has a `.name`);
          // cash sales/refunds eager-load `customer` (has a `.customer`
          // name field) - both are relation objects, not plain strings.
          return row.account?.name || row.customer?.customer || '-';
        },
        size: 200
      },
      {
        accessorKey: 'order_number',
        header: 'Order No.',
        cell: info => info.getValue() || '-',
        size: 120
      },
      {
        accessorKey: 'timestamp',
        header: 'Date',
        cell: info => {
          try {
            return format(new Date(info.getValue()), 'dd/MM/yyyy HH:mm');
          } catch (e) {
            return info.getValue();
          }
        },
        size: 150
      },
      {
        accessorKey: 'subtotal',
        header: 'Subtotal',
        cell: info => formatCurrency(info.getValue()),
        size: 100
      },
      {
        accessorKey: 'vat',
        header: 'VAT',
        cell: info => formatCurrency(info.getValue()),
        size: 100
      },
      {
        accessorKey: 'total',
        header: 'Total',
        cell: info => formatCurrency(info.getValue()),
        size: 100
      },
      ...(onView
        ? [
            {
              id: 'actions',
              header: 'Actions',
              cell: ({ row }) => (
                <IconButton
                  size="small"
                  onClick={() => onView(row.original)}
                  color="primary"
                  title="View"
                >
                  <ViewIcon fontSize="small" />
                </IconButton>
              ),
              size: 80
            }
          ]
        : [])
    ],
    [onView]
  );

  const table = useReactTable({
    data: data || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel()
  });

  if (!data || data.length === 0) {
    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
          {title}
        </Typography>
        <Paper elevation={1} sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {emptyMessage}
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
        {title}
      </Typography>

      <TableContainer component={Paper} elevation={2}>
        <Table size="small">
          <TableHead>
            {table.getHeaderGroups().map(headerGroup => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <TableCell
                    key={header.id}
                    sx={{ fontWeight: 'bold' }}
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableHead>

          <TableBody>
            {table.getRowModel().rows.map(row => (
              <TableRow key={row.id} hover>
                {row.getVisibleCells().map(cell => (
                  <TableCell key={cell.id}>
                    {flexRender(
                      cell.column.columnDef.cell,
                      cell.getContext()
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}

            {/* Totals Row */}
            {totals && (
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell colSpan={4} sx={{ fontWeight: 'bold', textAlign: 'right' }}>
                  TOTALS:
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  {formatCurrency(totals.subtotal || 0)}
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  {formatCurrency(totals.vat || 0)}
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  {formatCurrency(totals.total || 0)}
                </TableCell>
                {onView && <TableCell />}
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ReportTransactionsTable;
